import { toast } from "$global-providers/toast-provider";
import { gql, useMutation } from "@apollo/client";
import { useCallback, useState } from "react";
import { defineMessage, useIntl } from "react-intl";

const ERROR_MESSAGE = defineMessage({
  id: "invoicing.refund.error.msg",
  defaultMessage: "Failed to record refund",
});

const REFUND_PAYMENT = gql`
  mutation RefundInvoice($input: RefundInvoiceInput!) {
    refundInvoice(input: $input) {
      _id
      created
      updated
      invoiceStatus
      balance {
        total
        paid
        waived
        due
        exempt
        currency
      }
      transactions {
        _id
        actionedBy {
          person {
            firstname
            middlename
            lastname
          }
        }
        type
        timestamp
        amount {
          value
          currency
        }
        currencyExchange {
          from {
            currency
            value
          }
          to {
            currency
            value
          }
          exchangeRate {
            id
            baseCurrency
            quoteCurrency
            rate
            effectiveDate
          }
        }
      }
    }
  }
`;

export default function useRefundInvoice(invoiceId: string) {
  const { formatMessage } = useIntl();
  const [saving, setSaving] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);

  const [makeRefund] = useMutation(REFUND_PAYMENT, {
    onError() {
      toast.error(formatMessage(ERROR_MESSAGE), {});
    },
    onCompleted() {
      setSaving(false);
      setSaved(true);
    },
  });

  const refundInvoice = useCallback(
    async (values) => {
      setSaving(true);
      await makeRefund({
        variables: {
          input: {
            _id: invoiceId,
            ...values,
          },
        },
      });
    },
    [makeRefund, invoiceId],
  );

  return { refundInvoice, saving, saved };
}
